import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for the `editor:` scope (F67), following the exact
 * `documents:`/`web:` precedent (`apps/web/src/routes/documents/documents.i18n.ts`)
 * — NOT a second i18n runtime (E18/§13.3 ban), just physically scoped here
 * because `packages/i18n/**` is outside this task's ownership boundary.
 *
 * OPEN ISSUE: fold into `packages/i18n/src/catalogs/{en,ro}/editor.json`.
 */
const en = {
  'toolbar.label': 'Formatting',
  'content.titleLabel': 'Chapter title',
  'content.bodyLabel': 'Chapter body',
  'toolbar.bold': 'Bold',
  'toolbar.italic': 'Italic',
  'toolbar.heading': 'Heading',
  'toolbar.bulletList': 'Bulleted list',
  'toolbar.numberedList': 'Numbered list',
  'toolbar.blockquote': 'Quote',
  'toolbar.link': 'Link',
  'toolbar.linkPrompt': 'Link URL',
  'autosave.idle': 'No changes yet',
  'autosave.unsaved': 'Unsaved changes',
  'autosave.saving': 'Saving…',
  'autosave.saved': 'Saved · just now',
  'autosave.savedAt': 'Saved · {time}',
  'autosave.offlinePending': 'Offline · changes saved locally, will sync when back online',
  'autosave.error': 'Save failed',
  'autosave.retry': 'Retry',
  'conflict.title': 'This document changed elsewhere',
  'conflict.description': 'Someone else saved a newer version of “{title}” while you were editing. Reload to see the latest version before continuing — your unsaved changes stay in this tab until you do.',
  'conflict.reload': 'Reload',
  'restoreDraft.title': 'Restore your draft?',
  'restoreDraft.description': 'You have unsaved local changes to “{title}” from a previous session that were never synced.',
  'restoreDraft.restore': 'Restore draft',
  'restoreDraft.discard': 'Discard draft',
  'toast.draftRestored.action': 'Restored draft of',
  'toast.draftDiscarded.action': 'Discarded draft of',
  'readOnly.title': 'Read-only',
  'readOnly.description': 'You can view “{title}” but don’t have edit access. Only the owner or an editor collaborator can make changes here.',
  'versions.title': 'Version history',
  'versions.empty': 'No versions yet.',
  'versions.loadMore': 'Load more',
  'versions.current': 'Current',
  'versions.restoreAsNew': 'Restore as new version',
  'versions.restoreConfirm.action': 'Restore',
  'versions.origin.generate': 'Generated',
  'versions.origin.expand': 'Expanded',
  'versions.origin.cut_continue': 'Cut & continued',
  'versions.origin.difficulty_elevate': 'Difficulty changed',
  'versions.origin.translate': 'Translated',
  'versions.origin.edit': 'Edited',
  'versions.origin.restyle': 'Restyled',
  'versions.origin.imported': 'Imported',
  'toast.versionRestored.action': 'Restored an earlier version of',
  'export.title': 'Export',
  'export.description': 'Choose a format to export “{title}”.',
  'export.format.pdf': 'PDF',
  'export.format.docx': 'DOCX (Word)',
  'export.format.md': 'Markdown (.md)',
  'export.format.txt': 'Plain text (.txt)',
  'export.styleWarning': 'Markdown and plain text drop formatting — headings, bold, and links won’t carry over.',
  'export.start': 'Export',
  'export.preparing': 'Preparing your export…',
  'export.checkpointed': 'Checkpointed · you can close this and rejoin from the bell.',
  'export.ready': 'Your export is ready',
  'export.download': 'Download',
  'export.error': 'Export failed',
  'export.retry': 'Retry',
  'export.close': 'Close',
  'toast.exportReady.action': 'Exported',
  'error.notFound.title': 'We couldn’t find this document.',
  'error.notFound.cta': 'Back to documents',
  'error.generic.title': 'Couldn’t load the editor.',
  'error.generic.retry': 'Retry',
} as const;

const ro: Record<keyof typeof en, string> = {
  'toolbar.label': 'Formatare',
  'content.titleLabel': 'Titlul capitolului',
  'content.bodyLabel': 'Conținutul capitolului',
  'toolbar.bold': 'Aldin',
  'toolbar.italic': 'Cursiv',
  'toolbar.heading': 'Titlu',
  'toolbar.bulletList': 'Listă cu marcatori',
  'toolbar.numberedList': 'Listă numerotată',
  'toolbar.blockquote': 'Citat',
  'toolbar.link': 'Link',
  'toolbar.linkPrompt': 'Adresa linkului',
  'autosave.idle': 'Nicio modificare încă',
  'autosave.unsaved': 'Modificări nesalvate',
  'autosave.saving': 'Se salvează…',
  'autosave.saved': 'Salvat · adineaori',
  'autosave.savedAt': 'Salvat · {time}',
  'autosave.offlinePending': 'Offline · modificările sunt salvate local, se vor sincroniza la reconectare',
  'autosave.error': 'Salvarea a eșuat',
  'autosave.retry': 'Reîncearcă',
  'conflict.title': 'Acest document s-a schimbat în altă parte',
  'conflict.description': 'Altcineva a salvat o versiune mai nouă a documentului „{title}” în timp ce editai. Reîncarcă pentru a vedea ultima versiune înainte de a continua — modificările tale nesalvate rămân în acest tab până atunci.',
  'conflict.reload': 'Reîncarcă',
  'restoreDraft.title': 'Restaurezi ciorna?',
  'restoreDraft.description': 'Ai modificări locale nesalvate pentru „{title}” dintr-o sesiune anterioară care nu au fost sincronizate.',
  'restoreDraft.restore': 'Restaurează ciorna',
  'restoreDraft.discard': 'Renunță la ciornă',
  'toast.draftRestored.action': 'Ciorna a fost restaurată pentru',
  'toast.draftDiscarded.action': 'S-a renunțat la ciorna pentru',
  'readOnly.title': 'Doar citire',
  'readOnly.description': 'Poți vedea „{title}”, dar nu ai acces de editare. Doar proprietarul sau un colaborator editor poate face modificări aici.',
  'versions.title': 'Istoric versiuni',
  'versions.empty': 'Nicio versiune încă.',
  'versions.loadMore': 'Încarcă mai multe',
  'versions.current': 'Curentă',
  'versions.restoreAsNew': 'Restaurează ca versiune nouă',
  'versions.restoreConfirm.action': 'Restaurează',
  'versions.origin.generate': 'Generat',
  'versions.origin.expand': 'Extins',
  'versions.origin.cut_continue': 'Tăiat și continuat',
  'versions.origin.difficulty_elevate': 'Dificultate schimbată',
  'versions.origin.translate': 'Tradus',
  'versions.origin.edit': 'Editat',
  'versions.origin.restyle': 'Restilizat',
  'versions.origin.imported': 'Importat',
  'toast.versionRestored.action': 'S-a restaurat o versiune anterioară pentru',
  'export.title': 'Exportă',
  'export.description': 'Alege un format pentru a exporta „{title}”.',
  'export.format.pdf': 'PDF',
  'export.format.docx': 'DOCX (Word)',
  'export.format.md': 'Markdown (.md)',
  'export.format.txt': 'Text simplu (.txt)',
  'export.styleWarning': 'Markdown și textul simplu elimină formatarea — titlurile, textul aldin și linkurile nu se vor păstra.',
  'export.start': 'Exportă',
  'export.preparing': 'Se pregătește exportul…',
  'export.checkpointed': 'Checkpoint salvat · poți închide și reveni din clopoțel.',
  'export.ready': 'Exportul tău este gata',
  'export.download': 'Descarcă',
  'export.error': 'Exportul a eșuat',
  'export.retry': 'Reîncearcă',
  'export.close': 'Închide',
  'toast.exportReady.action': 'Exportat',
  'error.notFound.title': 'Nu am găsit acest document.',
  'error.notFound.cta': 'Înapoi la documente',
  'error.generic.title': 'Nu am putut încărca editorul.',
  'error.generic.retry': 'Reîncearcă',
};

export const EDITOR_CATALOG: Record<Locale, Record<keyof typeof en, string>> = { en, ro };

export type EditorCatalogKey = keyof typeof en;
